/**
 * Toolbar Customize Context Menu
 * Photoshop-style right-click menu on the toolbar: lists every toolbar item
 * (tool groups + bottom accessories) with a trailing checkmark to toggle its
 * visibility, plus a "Restore to Defaults" action that resets both visibility
 * and ordering. Rendered as its own fixed overlay (same pattern as
 * LayersContextMenu) with viewport flipping and outside/Escape dismissal.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
// Self-contained box model: the menu's fixed row metrics live in theme/
// toolbar.css (also imported by PanelShell/ToolbarCore; Vite dedupes), so
// hosts that mount this menu standalone still get the pinned shape.
import '../theme/toolbar.css';
import { usePanelLabel } from '../primitives/labels';
import {
    AlignEndVertical,
    AlignHorizontalJustifyCenter,
    AlignStartHorizontal,
    AlignStartVertical,
    AlignEndHorizontal,
    AlignVerticalJustifyCenter,
    Check,
    Lock,
    PanelLeft,
    PanelRight,
    PanelTop,
    PanelBottom,
} from '../primitives/icons';
import { useViewportClampedPosition } from '../primitives/useViewportClampedPosition';
import type { ToolbarMenuItem } from './useToolbarCustomization';
import type { DockSide } from '../store/useDockLayoutStore';
import type { IconAlignment } from '../store/useIconAlignmentStore';

const VIEWPORT_MARGIN = 8;

/** Docking edge picker rows shown under the "Toolbar Position" heading */
const POSITION_OPTIONS: Array<{
    position: DockSide;
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
}> = [
    { position: 'left', label: 'Left', Icon: PanelLeft },
    { position: 'right', label: 'Right', Icon: PanelRight },
    { position: 'top', label: 'Top', Icon: PanelTop },
    { position: 'bottom', label: 'Bottom', Icon: PanelBottom },
];

/** Icon alignment picker rows. Orientation-aware: a vertical rail packs
    top/center/bottom, a horizontal rail packs left/center/right, so each
    state shows exactly one label + one matching icon. */
const buildAlignmentOptions = (isHorizontal: boolean) =>
    isHorizontal
        ? [
              {
                  alignment: 'start' as IconAlignment,
                  label: 'Left',
                  Icon: AlignStartHorizontal,
              },
              {
                  alignment: 'center' as IconAlignment,
                  label: 'Center',
                  Icon: AlignHorizontalJustifyCenter,
              },
              {
                  alignment: 'end' as IconAlignment,
                  label: 'Right',
                  Icon: AlignEndHorizontal,
              },
          ]
        : [
              {
                  alignment: 'start' as IconAlignment,
                  label: 'Top',
                  Icon: AlignStartVertical,
              },
              {
                  alignment: 'center' as IconAlignment,
                  label: 'Center',
                  Icon: AlignVerticalJustifyCenter,
              },
              {
                  alignment: 'end' as IconAlignment,
                  label: 'Bottom',
                  Icon: AlignEndVertical,
              },
          ];

interface ToolbarCustomizeMenuProps {
    visible: boolean;
    x: number;
    y: number;
    items: ToolbarMenuItem[];
    locked: boolean;
    /** Current toolbar dock edge; drives the position picker checkmark */
    position: DockSide;
    /** Current icon alignment; drives the alignment picker checkmark */
    iconAlignment: IconAlignment;
    /** Rail orientation: the alignment labels/icons flip with it */
    isHorizontal: boolean;
    onToggleItem: (itemId: string, visible: boolean) => void;
    onToggleLock: (locked: boolean) => void;
    onPositionChange: (position: DockSide) => void;
    onIconAlignmentChange: (alignment: IconAlignment) => void;
    onRestoreDefaults: () => void;
    onClose: () => void;
    /** App-specific extra rows rendered at the bottom (after Restore
        Defaults), e.g. workbench-local layout switches that are not toolbar
        items. Rendered under its own divider with the same row styling. */
    footer?: React.ReactNode;
}

const ToolbarCustomizeMenu: React.FC<ToolbarCustomizeMenuProps> = ({
    visible,
    x,
    y,
    items,
    locked,
    position,
    iconAlignment,
    isHorizontal,
    onToggleItem,
    onToggleLock,
    onPositionChange,
    onIconAlignmentChange,
    onRestoreDefaults,
    onClose,
    footer,
}) => {
    const t = usePanelLabel();
    const menuRef = useRef<HTMLDivElement | null>(null);

    // Floating menus must always stay inside the main window; the hook
    // repositions the menu before paint when it would overflow the viewport.
    const anchor = visible ? { x, y } : null;
    const {
        ref: clampedRef,
        left,
        top,
    } = useViewportClampedPosition<HTMLDivElement>(anchor, {
        margin: VIEWPORT_MARGIN,
    });

    // Close on outside pointer down, Escape, or window blur
    useEffect(() => {
        if (!visible) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (
                menuRef.current &&
                event.target instanceof Node &&
                menuRef.current.contains(event.target)
            ) {
                return;
            }
            onClose();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('mousedown', handlePointerDown);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('blur', onClose);
        return () => {
            window.removeEventListener('mousedown', handlePointerDown);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('blur', onClose);
        };
    }, [visible, onClose]);

    if (!visible) return null;

    // Window-level overlay: portal to the document body so no host panel
    // (clipping overflow, stacking context, flex squeeze) can contain the
    // menu — the fixed coordinates stay viewport-relative either way, and
    // the menu always paints above the workspace like every other context
    // menu in the suite.
    return createPortal(
        <div
            ref={(node) => {
                menuRef.current = node;
                clampedRef.current = node;
            }}
            role="menu"
            aria-label={t('toolbar.customizeAria', 'Customize toolbar')}
            className="toolbar-customize-menu fixed z-50 flex flex-col rounded-md border border-[var(--border)] bg-[var(--popover)] text-[var(--popover-foreground)] shadow-lg select-none"
            style={{ left, top }}
            onContextMenu={(e) => e.preventDefault()}
        >
            {items.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={item.checked}
                    className="flex w-full cursor-pointer items-center whitespace-nowrap text-left hover:bg-[var(--highlight-color)]"
                    onClick={() => onToggleItem(item.id, !item.checked)}
                >
                    <span className="flex-1">
                        {t(`tools.${item.id}`, item.label)}
                    </span>
                    <Check
                        className={`flex-shrink-0 ${
                            item.checked ? 'opacity-100' : 'opacity-0'
                        }`}
                        strokeWidth={2.5}
                    />
                </button>
            ))}
            <div className="mx-1 my-[2px] border-t border-[var(--border)]" />
            {/* Docking edge picker: Photoshop-style movable toolbar. While the
               layout is locked the rows stay visible but inert (dimmed), so
               the menu shape never shifts between lock states. */}
            <div
                className="px-2 pt-1 pb-[2px] text-[10px] uppercase tracking-wide text-[var(--dimmed-foreground)] select-none"
                aria-hidden="true"
            >
                {t('toolbar.positionHeading', 'Toolbar Position')}
            </div>
            <div
                className={`flex flex-col ${
                    locked ? 'pointer-events-none opacity-40' : ''
                }`}
                aria-disabled={locked}
            >
                {POSITION_OPTIONS.map(({ position: edge, label, Icon }) => (
                    <button
                        key={edge}
                        type="button"
                        role="menuitemradio"
                        aria-checked={position === edge}
                        className="flex w-full cursor-pointer items-center whitespace-nowrap text-left hover:bg-[var(--highlight-color)]"
                        onClick={() => {
                            onPositionChange(edge);
                            onClose();
                        }}
                    >
                        <Icon className="flex-shrink-0" />
                        <span className="flex-1">
                            {t(`toolbar.position.${edge}`,
                                label,
                            )}
                        </span>
                        <Check
                            className={`flex-shrink-0 ${
                                position === edge ? 'opacity-100' : 'opacity-0'
                            }`}
                            strokeWidth={2.5}
                        />
                    </button>
                ))}
            </div>
            <div className="mx-1 my-[2px] border-t border-[var(--border)]" />
            {/* Icon alignment picker: which rail edge the icons pack toward.
               Labels follow the rail's orientation (vertical: Top/Center/
               Bottom, horizontal: Left/Center/Right). */}
            <div
                className="px-2 pt-1 pb-[2px] text-[10px] uppercase tracking-wide text-[var(--dimmed-foreground)] select-none"
                aria-hidden="true"
            >
                {t('toolbar.alignmentHeading', 'Icon Alignment')}
            </div>
            <div>
                {buildAlignmentOptions(isHorizontal).map(
                    ({ alignment, label, Icon }) => (
                        <button
                            key={alignment}
                            type="button"
                            role="menuitemradio"
                            aria-checked={iconAlignment === alignment}
                            className="flex w-full cursor-pointer items-center whitespace-nowrap text-left hover:bg-[var(--highlight-color)]"
                            onClick={() => {
                                onIconAlignmentChange(alignment);
                                onClose();
                            }}
                        >
                            <Icon className="flex-shrink-0" />
                            <span className="flex-1">
                                {t(`toolbar.align.${isHorizontal ? 'h' : 'v'}.${alignment}`,
                                    label,
                                )}
                            </span>
                            <Check
                                className={`flex-shrink-0 ${
                                    iconAlignment === alignment
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                }`}
                                strokeWidth={2.5}
                            />
                        </button>
                    ),
                )}
            </div>
            <div className="mx-1 my-[2px] border-t border-[var(--border)]" />
            <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={locked}
                className="flex w-full cursor-pointer items-center whitespace-nowrap text-left hover:bg-[var(--highlight-color)]"
                onClick={() => onToggleLock(!locked)}
            >
                <span className="flex-1">{t('toolbar.lockLayout', 'Lock Layout')}</span>
                <Lock
                    className={`flex-shrink-0 ${
                        locked ? 'opacity-100' : 'opacity-0'
                    }`}
                    strokeWidth={2.5}
                />
            </button>
            <button
                type="button"
                role="menuitem"
                className="flex w-full cursor-pointer items-center whitespace-nowrap text-left hover:bg-[var(--highlight-color)]"
                onClick={onRestoreDefaults}
            >
                <span className="flex-1">{t('toolbar.restoreDefaults', 'Restore to Defaults')}</span>
            </button>
            {footer ? (
                <>
                    <div className="mx-1 my-[2px] border-t border-[var(--border)]" />
                    {footer}
                </>
            ) : null}
        </div>,
        document.body,
    );
};

export default ToolbarCustomizeMenu;
